import { useCallback, useRef } from 'react'
import { supabase } from '@/integrations/supabase/client'

const DEBOUNCE_MS = 5000 // debounce frequent events like answer changes
const BATCH_MAX = 10

const eventQueue: { event_type: string; event_data?: Record<string, unknown> }[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

async function sendToEdge(action: string, payload: Record<string, unknown> = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) return

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-event`
  try {
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ action, ...payload }),
    })
  } catch {
    // silently fail — tracking should never block UX
  }
}

function flushQueue() {
  if (eventQueue.length === 0) return
  const batch = eventQueue.splice(0, BATCH_MAX)
  sendToEdge('batch_events', { events: batch })
}

export function trackSession() {
  sendToEdge('session')
}

export function trackEvent(event_type: string, event_data?: Record<string, unknown>) {
  eventQueue.push({ event_type, event_data })
  if (flushTimer) clearTimeout(flushTimer)
  if (eventQueue.length >= BATCH_MAX) {
    flushQueue()
  } else {
    flushTimer = setTimeout(flushQueue, DEBOUNCE_MS)
  }
}

export function trackEventImmediate(event_type: string, event_data?: Record<string, unknown>) {
  sendToEdge('event', { event_type, event_data })
}

export function useTracking() {
  const tracked = useRef(false)

  const trackOnce = useCallback((event_type: string, event_data?: Record<string, unknown>) => {
    if (tracked.current) return
    tracked.current = true
    trackEventImmediate(event_type, event_data)
  }, [])

  return { trackEvent, trackEventImmediate, trackSession, trackOnce }
}
